import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { DomainError } from '~/domain/errors/DomainError';
import { Image3D } from '~/domain/patient/image3d';
import { hasWorkerResampledImage } from '~/domain/patient/resamplingOrigin';
import { buildDeformedPreview } from '~/domain/services/buildDeformedPreview';
import { nextFreeSeriesNumber } from '~/domain/services/imageService';
import { useCommitImportToRepository } from '~/hooks/domain/useCommitImport';
import useLoadedSeriesNumbers from '~/hooks/image/useLoadedSeriesNumbers';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useActivateRegistration } from '~/hooks/registration/useRegistrationStore';
import {
    useDeformationDescribingTheScreen,
    useSelectedDeformableTransform,
} from '~/hooks/viewer/useSelectedDeformationField';
import { useActiveReplanningStage } from '~/hooks/useActiveReplanningStage';
import { usePrimaryRowComparison } from '~/hooks/viewer/usePrimaryRowComparison';
import {
    deformedPreviewCoverageState,
    deformedPreviewProgressState,
} from '~/state/atoms/deformedPreviewAtoms';
import { dicomProcessorState } from '~/state/atoms/dicomProcessorAtoms';
import { patientRepositoryState } from '~/state/atoms/patientAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { logErrorName } from '~/utils/logErrorName';

const cancelled = { current: false };

export function cancelDeformedPreview(): void {
    cancelled.current = true;
}

export function useDeformedPreview(): void {
    const patient = useSelectedPatient();
    const primaryImage = useSelectedPrimaryImage();
    const deformable = useDeformationDescribingTheScreen();
    const transform = useSelectedDeformableTransform();
    const dicomProcessor = useRecoilValue(dicomProcessorState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const commitImportToRepository = useCommitImportToRepository();
    const usedSeriesNumbers = useLoadedSeriesNumbers();
    const { show: showPair } = usePrimaryRowComparison();
    const activateRegistration = useActivateRegistration();
    const intl = useIntl();
    // A stage that compares doses side by side owns the secondary panel; a preview would replace it.
    const ownsSecondaryPanel =
        useActiveReplanningStage()?.comparesDoseSideBySide === true;

    const setProgress = useSetRecoilState(deformedPreviewProgressState);
    const setFieldCoverage = useSetRecoilState(deformedPreviewCoverageState);
    const built = useRef(new Map<string, Image3D>());
    const attempted = useRef(new Set<string>());

    const movingImage =
        deformable && patient && primaryImage
            ? patient.findCounterpartImage(deformable, primaryImage)
            : undefined;
    const show = useCallback(
        (preview: Image3D) => {
            if (!deformable) {
                return;
            }
            // Built in the primary image's frame, so the pair needs no registration.
            if (showPair(preview, undefined, 'apart')) {
                activateRegistration(
                    deformable.sopInstanceUid,
                    preview.seriesUid
                );
            }
        },
        [deformable, showPair, activateRegistration]
    );

    const workerResampled =
        !!primaryImage &&
        !!deformable &&
        !!patient &&
        hasWorkerResampledImage(
            patient.images,
            primaryImage.frameOfReferenceUid,
            deformable.sopInstanceUid
        );

    const runnable =
        !!primaryImage &&
        !!movingImage &&
        !!transform &&
        !!deformable &&
        !!dicomProcessor &&
        !!patientRepository &&
        !workerResampled &&
        !ownsSecondaryPanel;

    const build = useCallback(async () => {
        if (!runnable || !primaryImage || !movingImage || !transform) {
            return;
        }
        const key = deformable!.sopInstanceUid;
        const cached = built.current.get(key);
        if (cached) {
            show(cached);
            return;
        }
        cancelled.current = false;
        setProgress({ done: 0, total: primaryImage.grid.sizeZ });
        try {
            const preview = await buildDeformedPreview(
                primaryImage,
                movingImage,
                transform,
                nextFreeSeriesNumber(usedSeriesNumbers),
                {
                    isCancelled: () => cancelled.current,
                    onProgress: setProgress,
                }
            );
            if (!preview) {
                return;
            }
            for (const ds of preview.dicomDatasets) {
                if (dicomProcessor!.dicomToImageSlice(ds) === null) {
                    throw new Error('slice');
                }
            }
            const imported = commitImportToRepository(() =>
                dicomProcessor!.toRepository(patientRepository!)
            );
            // By series: the processor's result can hold more than this import.
            const image = imported?.images.find(
                (candidate) => candidate.seriesUid === preview.seriesInstanceUid
            );
            if (!image) {
                throw new Error('import');
            }
            // Marked before shown: export/save gates read it; keyed to its deformation.
            image.markAsSpatiallyResampled('approximated', [key]);
            built.current.set(key, image);
            setFieldCoverage(preview.fieldCoverage);
            show(image);
        } catch (error) {
            logErrorName('Deformed preview failed:', error);
            enqueueSnackbar(
                error instanceof DomainError
                    ? error.message
                    : intl.formatMessage({
                          id: 'registrationQa.preview.failed',
                      }),
                { variant: 'error' }
            );
        } finally {
            setProgress(null);
        }
    }, [
        runnable,
        primaryImage,
        movingImage,
        transform,
        deformable,
        usedSeriesNumbers,
        dicomProcessor,
        patientRepository,
        commitImportToRepository,
        show,
        intl,
        setProgress,
        setFieldCoverage,
    ]);

    const key = deformable?.sopInstanceUid;
    useEffect(() => {
        if (!runnable || !key || attempted.current.has(key)) {
            return;
        }
        attempted.current.add(key);
        void build();
    }, [runnable, key, build]);
}
